import React, { useCallback, useState } from 'react';

import { useMutation } from '@apollo/client/react';
import {
  Box,
  Card,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { isExternalTransactionSource } from '@realadvisor/common-types';
import { XIcon } from 'lucide-react';

import { DragHandle } from '@modules/_shared/components/sortable-list/DragHandle';
import { useSortableItem } from '@modules/_shared/components/sortable-list/useSortableItem';
import { UPDATE_CMA_PROPERTY_TRANSACTION_INCLUDE } from '@modules/reports/cma-reports/cmaReportsQueries';
import { useTransactionPresentation } from '@modules/reports/cma-reports/step-transaction/transactionPresentation';
import {
  TransactionInfo,
  TransactionInfoSkeleton,
  TransactionPrice,
  TransactionPriceSkeleton,
} from '@modules/reports/cma-reports/step-transaction/TransactionResultItem';
import type { CmaPropertyTransaction } from '@modules/reports/cma-reports/step-transaction/types';
import { useLocale } from '@shared/hooks/locale';

type TransactionCardProps = {
  cmaPropertyTransaction: CmaPropertyTransaction;
  index?: number;
  onRemoveById: (id: string) => Promise<void>;
  isDragOverlay?: boolean;
  isExternalHydrationLoading: boolean;
};

const TransactionCard = ({
  cmaPropertyTransaction,
  index,
  onRemoveById,
  isDragOverlay = false,
  isExternalHydrationLoading,
}: TransactionCardProps) => {
  const { t } = useLocale();
  const { setNodeRef, style, isDragging, dragHandleProps } = useSortableItem(
    cmaPropertyTransaction.id,
  );
  const theme = useTheme();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [updateInclude, { loading: updatingInclude }] = useMutation(
    UPDATE_CMA_PROPERTY_TRANSACTION_INCLUDE,
  );

  const presentation = useTransactionPresentation(cmaPropertyTransaction);

  const includeInMedian = cmaPropertyTransaction.include_in_median;
  const isExcluded = includeInMedian === false;

  const isExternal = isExternalTransactionSource(cmaPropertyTransaction.source);
  const isExternalLoading = isExternal && isExternalHydrationLoading;
  const isExternalUnavailable =
    isExternal &&
    !isExternalHydrationLoading &&
    cmaPropertyTransaction.transaction == null;

  const handleRemove = useCallback(
    async (evt: React.MouseEvent<HTMLButtonElement>) => {
      evt.stopPropagation();
      evt.preventDefault();
      setDeleteLoading(true);
      try {
        await onRemoveById(cmaPropertyTransaction.id);
      } finally {
        setDeleteLoading(false);
      }
    },
    [onRemoveById, cmaPropertyTransaction.id],
  );

  const handleToggleInclude = useCallback(
    async (checked: boolean) => {
      try {
        await updateInclude({
          variables: {
            cma_report_id: cmaPropertyTransaction.cma_report_id,
            id: cmaPropertyTransaction.id,
            include_in_median: checked,
          },
        });
      } catch (e) {
        console.error('Failed updating include_in_median', e);
      }
    },
    [
      cmaPropertyTransaction.cma_report_id,
      cmaPropertyTransaction.id,
      updateInclude,
    ],
  );

  return (
    <div
      ref={isDragOverlay ? undefined : setNodeRef}
      style={{
        ...style,
        position: 'relative',
      }}
    >
      {!isDragging && index !== undefined && (
        <Box
          sx={{
            position: 'absolute',
            top: -12,
            left: -12,
            height: 30,
            width: 30,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            borderRadius: '50%',
            color: 'background.paper',
            backgroundColor: (theme.vars ?? theme).palette.grey[600],
            border: '3px solid',
            borderColor: 'background.paper',
            zIndex: 1,
          }}
        >
          {index + 1}
        </Box>
      )}
      <Card
        variant="outlined"
        sx={{
          px: 2,
          py: 1,
          position: 'relative',
          background: 'var(--mui-palette-background-paper)',
          borderColor: isExcluded
            ? (theme.vars ?? theme).palette.warning.dark
            : undefined,
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ flex: 1, pr: 2 }}>
            {isExternalLoading && <TransactionInfoSkeleton />}
            {isExternalUnavailable && (
              <Typography variant="body2" color="text.secondary">
                {t('No data available')}
              </Typography>
            )}
            {!isExternalLoading && !isExternalUnavailable && (
              <TransactionInfo presentation={presentation} />
            )}
          </Box>

          <IconButton
            size="small"
            onClick={handleRemove}
            onMouseDown={mouseEvent => {
              mouseEvent.stopPropagation();
            }}
            disabled={deleteLoading}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              p: 0.5,
            }}
          >
            {deleteLoading ? (
              <CircularProgress
                sx={{
                  color: (theme.vars ?? theme).palette.text.secondary,
                  opacity: 0.5,
                }}
                size={20}
              />
            ) : (
              <XIcon size={20} />
            )}
          </IconButton>
        </Stack>

        <Box sx={{ mt: 1.5 }}>
          {isExternalLoading && <TransactionPriceSkeleton />}
          {!isExternalLoading && !isExternalUnavailable && (
            <TransactionPrice
              priceLabel={presentation.priceLabel}
              pricePerSqmLabel={presentation.pricePerSqmLabel}
            />
          )}
        </Box>

        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeInMedian}
                onChange={(_, checked) => handleToggleInclude(checked)}
                disabled={updatingInclude}
              />
            }
            label={t('Include in price calculation')}
          />
        </Box>

        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
          }}
        >
          <DragHandle
            attributes={dragHandleProps.attributes}
            listeners={dragHandleProps.listeners}
            isDragging={isDragging}
            variant="icon-button"
            rotateIcon
          />
        </Box>
      </Card>
    </div>
  );
};

export default TransactionCard;
